// InheritancePlanningArticle.tsx
import { Col, Row } from "react-bootstrap";
import MaterialIcon from "../../material-icon";
import { colorPalette } from "../../../utils/theme";
import useIsMobile from "../../../hooks/useIsMobile";
import { ArticleImage, ArticleTitle, ArticleSubtitle, SharingContainer, ArticleDescription, Headline, ThreeDots, BulletPointList, BulletPointItem, ArticleText, FilterContainer, StyledLink, AuthorRole, AuthorAvatar, AuthorName, SubHeadline, Divider, ArticleImageContainer, PlayButton, YouTubeFrame, VideoLoadingContainer } from "../article.styles";
import InheritancePlanningImage from "../../../assets/images/articles/inheritance-planning-large.webp";
import InheritancePlanningMobileImage from "../../../assets/images/articles/inheritance-planning-mobile.webp";
import Filter from "../../filter/filter";
import CTAButton from "../../cta-button/cta-button";
import { useCallback, useState } from "react";
import { useTranslation } from 'react-i18next';
import { openExternalLink } from "../../../utils/navigation";

interface InheritancePlanningArticleProps {
    onClickShare: () => void;
}

const InheritancePlanningArticle: React.FC<InheritancePlanningArticleProps> = ({ onClickShare }) => {
    const isMobile = useIsMobile();
    const { t } = useTranslation();

    // States
    const [showVideo, setShowVideo] = useState(false);
    const [videoLoaded, setVideoLoaded] = useState(false);

    const handlePlayButtonClick = () => {
        setShowVideo(true);
    };

    const handleVideoLoad = () => {
        setVideoLoaded(true);
    };

    const renderHeaderContent = useCallback(() => {
        return (
            <ArticleImageContainer>
                {!showVideo ? (
                    <>
                        <ArticleImage
                            src={isMobile ? InheritancePlanningMobileImage : InheritancePlanningImage}
                            alt="Article Thumbnail"
                            showBorderRadius={!isMobile}
                        />
                        <PlayButton onClick={handlePlayButtonClick}>
                            <MaterialIcon icon="play_arrow" size={48} color={colorPalette.white} />
                        </PlayButton>
                    </>
                ) : (
                    <>
                        {/* Show loading container until the video is fully loaded */}
                        {!videoLoaded && (
                            <VideoLoadingContainer>
                                <PlayButton>
                                    <MaterialIcon icon="play_arrow" size={48} color={colorPalette.white} />
                                </PlayButton>
                            </VideoLoadingContainer>
                        )}

                        {/* Render YouTubeFrame initially with visibility control */}
                        <YouTubeFrame
                            src="https://www.youtube-nocookie.com/embed/XZ0u551hldM?autoplay=1"
                            title="DGMA Legal YouTube Video"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                            showBorderRadius={!isMobile}
                            onLoad={handleVideoLoad}
                            style={{ display: videoLoaded ? 'block' : 'none' }}
                        />
                    </>
                )}
            </ArticleImageContainer>
        );
    }, [videoLoaded, showVideo]);

    return (
        <>
            {isMobile && (
                renderHeaderContent()
            )}

            <Row className="justify-content-center flex-grow-1" style={{ paddingLeft: 16, paddingRight: 16 }}>
                <Col xs={12} lg={8} xl={6}>
                    <ArticleTitle>{t("pages.knowledge_hub.articles.inheritance_planning.title")}</ArticleTitle>
                    <ArticleSubtitle>{t("articles.inheritance_planning.subtitle")}</ArticleSubtitle>

                    <SharingContainer>
                        <div className="d-flex align-items-center" style={{ gap: 8 }}>
                            <AuthorAvatar />
                            <div>
                                <AuthorName>DGMA Legal Services</AuthorName>
                                <AuthorRole>Partner Content</AuthorRole>
                            </div>
                        </div>
                        <MaterialIcon icon="share" size={24} color={colorPalette.textDark} onClick={onClickShare} />
                    </SharingContainer>

                    {!isMobile && (
                        renderHeaderContent()
                    )}

                    <ArticleDescription>{t("articles.inheritance_planning.intro1")}</ArticleDescription>
                    <ArticleDescription>{t("articles.inheritance_planning.intro2")}</ArticleDescription>

                    <ThreeDots />

                    <Headline>{t("articles.inheritance_planning.sharia_title")}</Headline>
                    <BulletPointList>
                        <BulletPointItem>{t("articles.inheritance_planning.sharia1")}</BulletPointItem>
                        <BulletPointItem>{t("articles.inheritance_planning.sharia2")}</BulletPointItem>
                        <BulletPointItem>{t("articles.inheritance_planning.sharia3")}</BulletPointItem>
                        <BulletPointItem>{t("articles.inheritance_planning.sharia4")}</BulletPointItem>
                        <BulletPointItem>
                            {t("articles.inheritance_planning.sharia5_prefix")}&nbsp;
                            <StyledLink href="https://www.tamm.abudhabi/wb/doh/inheritance-calculator?lang=en" target="_blank" rel="noopener noreferrer">
                                {t("articles.inheritance_planning.sharia5_link")}
                            </StyledLink>
                            &nbsp;{t("articles.inheritance_planning.sharia5_suffix")}
                        </BulletPointItem>
                    </BulletPointList>

                    <ThreeDots />

                    <Headline>{t("articles.inheritance_planning.no_will_title")}</Headline>
                    <ArticleText>{t("articles.inheritance_planning.no_will1")}</ArticleText>
                    <ArticleText>{t("articles.inheritance_planning.no_will2")}</ArticleText>
                    <ArticleText>{t("articles.inheritance_planning.no_will3")}</ArticleText>
                    <ArticleText>{t("articles.inheritance_planning.no_will4")}</ArticleText>
                    <ArticleText>{t("articles.inheritance_planning.no_will5")}</ArticleText>
                    <ArticleText>{t("articles.inheritance_planning.no_will6")}</ArticleText>

                    <ThreeDots />

                    <Headline>{t("articles.inheritance_planning.solution_title")}</Headline>
                    <ArticleText>{t("articles.inheritance_planning.solution1")}</ArticleText>
                    <ArticleText>{t("articles.inheritance_planning.solution2")}</ArticleText>

                    <SubHeadline>{t("articles.inheritance_planning.who_title")}</SubHeadline>
                    <ArticleText>{t("articles.inheritance_planning.who_text")}</ArticleText>

                    <SubHeadline>{t("articles.inheritance_planning.what_title")}</SubHeadline>
                    <ArticleText>{t("articles.inheritance_planning.what_text")}</ArticleText>

                    <SubHeadline>{t("articles.inheritance_planning.must_live_title")}</SubHeadline>
                    <ArticleText>{t("articles.inheritance_planning.must_live_text")}</ArticleText>

                    <Divider />

                    <Headline>{t("articles.inheritance_planning.cta_title")}</Headline>
                    <CTAButton
                        variant="primary"
                        size="M"
                        label={t("pages.knowledge_hub.articles.inheritance_planning.book_free_consultation")}
                        icon="open_in_new"
                        onClick={() => openExternalLink("https://calendly.com/dgma-legal/erstberatung-testamente")}
                        fullWidth={isMobile}
                    />

                    <Divider />

                    <SubHeadline>{t("pages.knowledge_hub.articles.inheritance_planning.about_dgma")}</SubHeadline>
                    <ArticleText>{t("articles.inheritance_planning.about_dgma_text1")}</ArticleText>
                    <ArticleText>{t("articles.inheritance_planning.about_dgma_text2")}</ArticleText>

                    <CTAButton
                        variant="ghost"
                        size="L"
                        label={t("pages.knowledge_hub.articles.inheritance_planning.visit_dgma")}
                        icon="open_in_new"
                        onClick={() => openExternalLink("https://dgma-legal.com")}
                        noPadding
                    />

                    {/*<FilterContainer>
                        <Filter label="Handover" disabled />
                        <Filter label="Mietverwaltung" disabled />
                        <Filter label="Services" disabled />
                    </FilterContainer>*/}
                </Col>
            </Row>
        </>
    );
};

export default InheritancePlanningArticle;
